import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Receipt,
  CreditCard,
  Calendar
} from 'lucide-react'
// import { expenseAPI } from '../services/api' // Removed API calls
import { LoadingSpinner } from '../components'
import toast from 'react-hot-toast'

function Expenses() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingExpense, setEditingExpense] = useState(null)

  const queryClient = useQueryClient()

  // Fetch expenses
  const { data: expensesData, isLoading } = useQuery(
    ['expenses', { page: currentPage, search: searchTerm, category: selectedCategory }],
    () => expenseAPI.getAll({
      page: currentPage,
      limit: 10,
      search: searchTerm,
      category: selectedCategory,
    })
  )

  // Create expense mutation
  const createExpenseMutation = useMutation(expenseAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('expenses')
      setShowModal(false)
      toast.success('Expense created successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create expense')
    },
  })

  // Update expense mutation
  const updateExpenseMutation = useMutation(
    ({ id, data }) => expenseAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('expenses')
        setShowModal(false)
        setEditingExpense(null)
        toast.success('Expense updated successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update expense')
      },
    }
  )

  // Delete expense mutation
  const deleteExpenseMutation = useMutation(expenseAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('expenses')
      toast.success('Expense deleted successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete expense')
    },
  })

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  const onSubmit = (data) => {
    if (editingExpense) {
      updateExpenseMutation.mutate({ id: editingExpense.id, data })
    } else {
      createExpenseMutation.mutate(data)
    }
  }

  const handleEdit = (expense) => {
    setEditingExpense(expense)
    reset({
      description: expense.description,
      amount: expense.amount,
      category: expense.category,
      expenseDate: expense.expenseDate,
      vendorName: expense.vendorName,
      paymentMethod: expense.paymentMethod,
      notes: expense.notes,
    })
    setShowModal(true)
  }

  const handleDelete = (expense) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      deleteExpenseMutation.mutate(expense.id)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingExpense(null)
    reset()
  }

  const expenses = expensesData?.data?.expenses || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-secondary-900">Expenses</h1>
          <p className="mt-1 text-base text-secondary-600">
            Track and manage business expenses
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Expense
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search expenses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            <div>
              <label className="label">Category</label>
              <input
                type="text"
                placeholder="Filter by category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="input"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedCategory('')
                }}
                className="btn-outline w-full"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="card">
        <div className="card-content p-0">
          {isLoading ? (
            <LoadingSpinner size="lg" className="h-64" />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr className="table-row">
                    <th className="table-head">Description</th>
                    <th className="table-head">Amount</th>
                    <th className="table-head">Category</th>
                    <th className="table-head">Vendor</th>
                    <th className="table-head">Payment Method</th>
                    <th className="table-head">Date</th>
                    <th className="table-head">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {expenses.map((expense) => (
                    <tr key={expense.id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <Receipt className="h-4 w-4 text-primary-600" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-secondary-900">
                              {expense.description}
                            </div>
                            <div className="text-sm text-secondary-500">
                              {expense.notes}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center text-sm text-secondary-900">
                          <CreditCard className="h-4 w-4 mr-1" />
                          ₹{expense.amount}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="badge badge-secondary">
                          {expense.category || 'Uncategorized'}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          {expense.vendorName || 'N/A'}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="badge badge-secondary">
                          {expense.paymentMethod || 'N/A'}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center text-sm text-secondary-900">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(expense.expenseDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(expense)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(expense)}
                            className="text-danger-600 hover:text-danger-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Expense Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="modal-overlay" onClick={handleCloseModal} />
            <div className="modal-content max-w-2xl">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                {editingExpense ? 'Edit Expense' : 'Add New Expense'}
              </h3>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="label">Description</label>
                  <input
                    {...register('description', { required: 'Description is required' })}
                    className="input"
                    placeholder="Enter expense description"
                  />
                  {errors.description && (
                    <p className="form-error">{errors.description.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Amount</label>
                    <input
                      {...register('amount', { required: 'Amount is required' })}
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="Enter amount"
                    />
                    {errors.amount && (
                      <p className="form-error">{errors.amount.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Category</label>
                    <input
                      {...register('category')}
                      className="input"
                      placeholder="Enter category"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Vendor Name</label>
                    <input
                      {...register('vendorName')}
                      className="input"
                      placeholder="Enter vendor name"
                    />
                  </div>
                  <div>
                    <label className="label">Payment Method</label>
                    <select
                      {...register('paymentMethod')}
                      className="input"
                    >
                      <option value="">Select payment method</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="label">Expense Date</label>
                  <input
                    {...register('expenseDate', { required: 'Expense date is required' })}
                    type="date"
                    className="input"
                  />
                  {errors.expenseDate && (
                    <p className="form-error">{errors.expenseDate.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">Notes</label>
                  <textarea
                    {...register('notes')}
                    className="input"
                    rows={3}
                    placeholder="Enter additional notes"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createExpenseMutation.isLoading || updateExpenseMutation.isLoading}
                    className="btn-primary"
                  >
                    {createExpenseMutation.isLoading || updateExpenseMutation.isLoading ? (
                      <LoadingSpinner size="sm" className="mr-2" />
                    ) : null}
                    {editingExpense ? 'Update Expense' : 'Create Expense'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Expenses
